import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import MiniToggle from './MiniToggle';
import { ALL_MODULE_KEYS, MODULE_LABELS, type ModuleKey } from '../../constants/modules';
import type { TenantUser } from './ClientUsersTab';

interface Props {
  tenantId: string;
  enabledModules: ModuleKey[];
  onTenantUpdated: (patch: { enabledModules: ModuleKey[] }) => void;
}

export default function ClientModuleAccessTab({ tenantId, enabledModules, onTenantUpdated }: Props) {
  const { t } = useTranslation();
  const [users, setUsers]     = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(true);

  const enabledSet = new Set(enabledModules);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/admin/tenants/${tenantId}/users`);
      setUsers(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tenantId]);

  const toggleTenantModule = async (key: ModuleKey) => {
    const next = enabledSet.has(key) ? enabledModules.filter(k => k !== key) : [...enabledModules, key];
    const { data } = await api.put(`/admin/tenants/${tenantId}`, { enabledModules: next });
    onTenantUpdated({ enabledModules: data.enabledModules });
  };

  const toggleUserModule = async (u: TenantUser, key: ModuleKey) => {
    const restricted = new Set(u.restrictedModules ?? []);
    if (restricted.has(key)) restricted.delete(key); else restricted.add(key);
    await api.patch(`/admin/tenants/${tenantId}/users/${u.id}`, { restrictedModules: [...restricted] });
    load();
  };

  return (
    <div>
      <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>{t('admin.moduleAccess.title')}</h2>
      <p style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 16 }}>{t('admin.moduleAccess.tenantHint')}</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10, marginBottom: 28 }}>
        {ALL_MODULE_KEYS.map(key => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--white)', borderRadius: 10, border: '1px solid var(--border)' }}>
            <span style={{ fontSize: 15 }}>{MODULE_LABELS[key].icon}</span>
            <span style={{ fontSize: 12, flex: 1 }}>{t(MODULE_LABELS[key].labelKey)}</span>
            <MiniToggle value={enabledSet.has(key)} onChange={() => toggleTenantModule(key)} title={t(MODULE_LABELS[key].labelKey)} />
          </div>
        ))}
      </div>

      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{t('admin.moduleAccess.perUser')}</div>
      <p style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 12 }}>{t('admin.moduleAccess.perUserHint')}</p>

      {loading ? (
        <div style={{ color: 'var(--gray)', fontSize: 13, padding: '12px 0' }}>...</div>
      ) : users.filter(u => u.role !== 'super_admin').length === 0 ? (
        <div style={{ color: 'var(--gray)', fontSize: 13, padding: '12px 0' }}>{t('tenants.usersModal.noUsers')}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {users.filter(u => u.role !== 'super_admin').map(u => {
            const restricted = new Set(u.restrictedModules ?? []);
            return (
              <div key={u.id} style={{ background: 'var(--white)', borderRadius: 12, border: '1px solid var(--border)', padding: '12px 16px' }}>
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>{u.name || u.email}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                  {ALL_MODULE_KEYS.map(key => {
                    const tenantAllows = enabledSet.has(key);
                    const granted = tenantAllows && !restricted.has(key);
                    return (
                      <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 13 }}>{MODULE_LABELS[key].icon}</span>
                        <span style={{ fontSize: 10, color: 'var(--gray)' }}>{t(MODULE_LABELS[key].labelKey)}</span>
                        <MiniToggle
                          value={granted}
                          disabled={!tenantAllows}
                          onChange={() => toggleUserModule(u, key)}
                          title={tenantAllows ? t(MODULE_LABELS[key].labelKey) : t('admin.moduleAccess.tenantDisabled')}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
